import _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import {createStructuredSelector as reselectCreateStructuredSelector} from 'reselect';

import {
  CommonAppParametricSelector,
  CommonAppSelector,
} from '../../libs/shared/core/src/store/commonAppSelectorTypes';
import {
  createCommonDeepEqualSelector,
  createCommonSelector,
  createCommonShallowEqualSelector,
  createCommonTailStrictEqualSelector,
  makeCommonMapDispatchToProps,
  makeCommonMapStateToProps,
} from '../../libs/shared/core/src/store/helpers/commonReselectHelpers';
import {CreateSelectorOptions} from '../../libs/shared/core/src/store/helpers/selectorInstrumentation';
import {AppAction} from '../../src/store/appActions';
import {AppState} from '../../src/store/appState';

/*
 * Reselect.
 */

export type AppSelector<T> = CommonAppSelector<AppState, T>;
export type AppParametricSelector<P, T> = CommonAppParametricSelector<AppState, P, T>;

// TODO: Update these types to pass AppState instead of being fully generic.
export const createSelector = createCommonSelector;
export const createDeepEqualSelector = createCommonDeepEqualSelector;
export const createShallowEqualSelector = createCommonShallowEqualSelector;
export const createTailStrictEqualSelector = createCommonTailStrictEqualSelector;

/** Wrap the output of a selector in a new deep equal selector. */
export function wrapInDeepEqualSelector<R>(
  selector: AppSelector<R>,
  options?: CreateSelectorOptions,
): AppSelector<R>;
export function wrapInDeepEqualSelector<P, R>(
  selector: AppParametricSelector<P, R>,
  options?: CreateSelectorOptions,
): AppParametricSelector<P, R> {
  return createDeepEqualSelector([selector], (value) => value, options);
}

/** Wrap the output of a selector in a new shallow selector. */
export function wrapInShallowSelector<R>(
  selector: AppSelector<R>,
  options?: CreateSelectorOptions,
): AppSelector<R>;
export function wrapInShallowSelector<P, R>(
  selector: AppParametricSelector<P, R>,
  options?: CreateSelectorOptions,
): AppParametricSelector<P, R> {
  return createShallowEqualSelector([selector], (value) => value, options);
}

/** Simpler createStructuredSelector. */
export function createStructuredSelector<P, T>(selectors: {[K in keyof T]: AppParametricSelector<P, T[K]>}) {
  return reselectCreateStructuredSelector(selectors);
}

/** Wrap the array output of a selector into a set. */
export function wrapInArrayToSetSelector<P, R>(
  getArray: AppParametricSelector<P, ReadonlyArray<R>>,
  options?: CreateSelectorOptions,
) {
  return createSelector([getArray], (array) => new Set(array), options);
}

/** Allow memoization selectors mapped from an array. */
export function createDistributedSelector<P, TMapped extends string | number, TResult>(
  selector: AppParametricSelector<P, ReadonlyArray<TMapped>>,
  selectorCreator: (value: TMapped) => AppParametricSelector<P, TResult>,
  options?: CreateSelectorOptions,
) {
  // Cache the previously created selectors (by mapped value).
  let cachedSelectors: {[K: string]: AppParametricSelector<P, TResult> | undefined} = {};

  // Memoize the selectors for a given array of values.
  const getSelectors = createSelector(
    [wrapInDeepEqualSelector(selector, options)],
    (values) => {
      const selectors = values.map((value) => {
        const cachedSelector = cachedSelectors[value.toString()];
        return {
          value,
          selector: cachedSelector || selectorCreator(value),
        };
      });

      cachedSelectors = _(selectors)
        .keyBy((s) => s.value)
        .mapValues((s) => s.selector)
        .value();
      return selectors.map((s) => s.selector);
    },
    options,
  );

  // Always re-evaluate every single selector.
  return (state: AppState, props: P) => {
    const selectors = getSelectors(state, props);
    return selectors.map((s) => s(state, props));
  };
}

export function makeMapStateToProps<P = {}>() {
  return makeCommonMapStateToProps<P, AppState>();
}

export function makeMapDispatchToProps<P = {}>() {
  return makeCommonMapDispatchToProps<P, AppState, AppAction>();
}
